import React, { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

/**
 * Debounced free-text filter.
 *
 * Exposes `flush()` via ref so a parent can push a pending (debounced) value
 * through synchronously before acting on the filter state.
 */
const TextFilter = forwardRef(function TextFilter({ label, value, onChange, placeholder = 'Project Name' }, ref) {
  const [inputValue, setInputValue] = useState(value || '');
  const timeoutRef = useRef(null);
  // Value waiting in the debounce timer (null when nothing is pending).
  const pendingValueRef = useRef(null);
  // Last value this component emitted via onChange, so the sync effect below
  // can tell an external change apart from the echo of our own update.
  const lastEmittedRef = useRef(value || '');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const cancelPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingValueRef.current = null;
  }, []);

  // Keep the input in sync when the filter is changed from outside (e.g.
  // "Clear all" in the mobile filters sheet or loading a saved search), and
  // drop any pending debounced update so it can't re-apply the old text.
  // Our own debounced updates echo back through `value` and are ignored so
  // keystrokes typed during the debounce window are not lost.
  useEffect(() => {
    const next = value || '';
    if (next === lastEmittedRef.current) return;
    lastEmittedRef.current = next;
    cancelPending();
    setInputValue(next);
  }, [value, cancelPending]);

  // Push a pending debounced value through immediately (no-op if none).
  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pendingValueRef.current !== null) {
      const pending = pendingValueRef.current;
      pendingValueRef.current = null;
      lastEmittedRef.current = pending;
      onChangeRef.current(pending);
    }
  }, []);

  // Drop any pending value and empty the input without emitting. For
  // parents that reset the filter themselves (e.g. "Clear all"), where the
  // `value` prop may not change if the committed title was already empty.
  const clear = useCallback(() => {
    cancelPending();
    lastEmittedRef.current = '';
    setInputValue('');
  }, [cancelPending]);

  useImperativeHandle(ref, () => ({ flush, clear }), [flush, clear]);

  // Flush (not drop) a pending update on unmount, e.g. when the mobile
  // filters sheet closes mid-debounce, so the last keystrokes still apply.
  useEffect(() => flush, [flush]);

  // Debounced onChange to avoid filtering on every keystroke
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    cancelPending();
    pendingValueRef.current = newValue;
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      pendingValueRef.current = null;
      lastEmittedRef.current = newValue;
      // Use the latest onChange, not the one captured at keystroke time: the
      // parent rebuilds its filter object from its own closure, so a stale
      // callback would revert any filter changed during the debounce window.
      onChangeRef.current(newValue);
    }, 300);
  }, [cancelPending]);

  const handleClear = useCallback(() => {
    cancelPending();
    setInputValue('');
    lastEmittedRef.current = '';
    onChange('');
  }, [onChange, cancelPending]);

  return (
    <div className="text-filter">
      <input
        type="text"
        className="text-filter-input"
        value={inputValue}
        onChange={handleChange}
        placeholder={placeholder}
        aria-label={label}
      />
      {inputValue && (
        <button
          className="text-filter-clear"
          onClick={handleClear}
          aria-label="Clear search"
        >
          <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor">
            <path d="M9.53 3.53a.75.75 0 0 0-1.06-1.06L6 4.94 3.53 2.47a.75.75 0 0 0-1.06 1.06L4.94 6 2.47 8.47a.75.75 0 1 0 1.06 1.06L6 7.06l2.47 2.47a.75.75 0 1 0 1.06-1.06L7.06 6l2.47-2.47Z"/>
          </svg>
        </button>
      )}
    </div>
  );
});

export default TextFilter;

